/**
 * useLivePreview (TMMPDM-0046, UI-010) — the server's dry run beside an editor, shared by the
 * reseller card and commission plan editors. The verdict on screen is always the server's for the
 * body on screen, never a previous body's:
 *
 * - The page hands over `bodyKey`, the JSON of the body the check sends (the page builds it; this
 *   hook only parses it back, so the request body is exactly the page's), or `''` when the page
 *   has nothing to send. While it is `''` the verdict and example are cleared.
 * - `ready` holds every check back (the plan editor waits for the reseller's rate cards).
 * - The first check runs as soon as the page is ready, so a Draft opens with its verdict; after
 *   that, a check runs 600 ms after the last edit, and `run` is Check's immediate one.
 * - `edit` is called on every edit: the previous answer (verdict, example and the dry run's field
 *   messages) is dropped, any answer still out for an older body is ignored, and the debounced
 *   check is re-armed. Until its answer lands the preview is `checking` — pending, never a verdict.
 * - `cancelPending` (Save) stops a waiting debounced check.
 * - An answer updates the preview only. It never clears a Save's inline messages; the page keeps
 *   those until the field is edited or the next Save (FRM-005, TMMPDM-0049).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** The debounced dry run waits this long after the last edit. */
export const PREVIEW_DEBOUNCE_MS = 600;

export interface LivePreview<Body, Result> {
  /** The answer for the body on screen; `null` before one, after an edit, or while invalid. */
  result: Result | null;
  failure: { error: unknown } | null;
  /** A check is out, or an edit's debounced check is waiting: the panel shows it pending. */
  checking: boolean;
  /** A request is actually out (Check is disabled meanwhile). */
  inFlight: boolean;
  /** Check now with this body (the latest request wins). */
  run: (body: Body) => void;
  /** An edit happened: drop the previous answer and any still out; re-arm the check. */
  edit: () => void;
  /** Save: the waiting debounced check never fires. */
  cancelPending: () => void;
}

export function useLivePreview<Body, Result>({
  bodyKey,
  ready,
  request,
}: {
  bodyKey: string;
  ready: boolean;
  /** Stable (`useCallback`): a new identity re-runs the check. */
  request: (body: Body) => Promise<Result>;
}): LivePreview<Body, Result> {
  const [result, setResult] = useState<Result | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [inFlight, setInFlight] = useState(false);
  // An edit's check is waiting for its debounce.
  const [waiting, setWaiting] = useState(false);
  // Every edit re-arms the debounced check, even one that leaves the body as it was: the edit
  // dropped the answer on screen and any still out, so the body on screen is checked again.
  const [edits, setEdits] = useState(0);
  const seq = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  // Inputs the client already rejects: no verdict or example for a body that can't be sent.
  if (bodyKey === '' && (result !== null || failure !== null)) {
    setResult(null);
    setFailure(null);
  }

  const run = useCallback(
    async (body: Body) => {
      seq.current += 1;
      const mine = seq.current;
      setWaiting(false);
      setInFlight(true);
      setFailure(null);
      try {
        const answer = await request(body);
        if (mine !== seq.current) return;
        setResult(answer);
      } catch (err) {
        if (mine !== seq.current) return;
        setResult(null);
        setFailure({ error: err });
      } finally {
        if (mine === seq.current) setInFlight(false);
      }
    },
    [request],
  );

  useEffect(() => {
    if (!ready || bodyKey === '') return;
    timer.current = window.setTimeout(
      () => void run(JSON.parse(bodyKey) as Body),
      edits > 0 ? PREVIEW_DEBOUNCE_MS : 0,
    );
    return () => window.clearTimeout(timer.current);
  }, [ready, edits, bodyKey, run]);

  return {
    result,
    failure,
    checking: inFlight || waiting,
    inFlight,
    run: (body) => void run(body),
    edit: () => {
      setEdits((n) => n + 1);
      seq.current += 1;
      setInFlight(false);
      setWaiting(true);
      setResult(null);
      setFailure(null);
    },
    cancelPending: () => {
      window.clearTimeout(timer.current);
      setWaiting(false);
    },
  };
}
